import { unBuildPrompt } from './agentInsightsUtils';

export const CONVERSATION_TITLE_MAX = 64;
export const RECENT_CHAT_TITLE_MAX = 80;
export const NEW_CHAT_PLACEHOLDER = 'New chat';

const ELLIPSIS = '\u2026';
const MARKDOWN_LINK_REGEX = /@?\[([^\]]+)\]\([^)]+\)/g;

export interface ConversationTitleSource {
  title?: string | null;
  message_preview?: string | null;
}

export function stripConversationTitleLinks(text: string): string {
  return text.replace(MARKDOWN_LINK_REGEX, '$1');
}

/**
 * A conversation message's `content` is the `Any`-typed Claude content, not a
 * string: a plain string, a `{ text }` block, or a `{ content: [{ type: 'text',
 * text }] }` block array (mirrors the backend's preview extraction). Pull the
 * first text out so we never hand a structured value to `unBuildPrompt`.
 */
function isTextBlock(item: unknown): item is { type: string; text: string } {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    item.type === 'text' &&
    'text' in item &&
    typeof item.text === 'string'
  );
}

export function extractContentText(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    const block = content.find(isTextBlock);
    return block?.text ?? '';
  }
  if (content && typeof content === 'object') {
    if ('text' in content && typeof content.text === 'string') {
      return content.text;
    }
    if ('content' in content) {
      return extractContentText(content.content);
    }
  }
  return '';
}

/**
 * Prefer the model-generated title; fall back to the first-user-message preview
 * (already stored, or extracted from thread history) then `placeholder`.
 */
export function resolveConversationTitle(
  conversation: ConversationTitleSource | undefined,
  options?: { placeholder?: string; maxLength?: number },
): string {
  const placeholder = options?.placeholder ?? NEW_CHAT_PLACEHOLDER;
  const maxLength = options?.maxLength ?? CONVERSATION_TITLE_MAX;
  const title = conversation?.title?.trim() ?? '';
  const preview = conversation?.message_preview ? unBuildPrompt(conversation.message_preview).trim() : '';
  const text = stripConversationTitleLinks(title || preview || placeholder);
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}${ELLIPSIS}` : text;
}

export function firstUserMessageText(
  messages: Array<{ role: string; content: unknown; turn: number }> | undefined,
): string {
  if (!messages?.length) {
    return '';
  }
  const firstUserMessage = [...messages].sort((a, b) => a.turn - b.turn).find((message) => message.role === 'user');
  return firstUserMessage ? extractContentText(firstUserMessage.content) : '';
}

/**
 * `conversation_history` has no title field. Reloading the same thread must
 * keep a model title that came from the history list; switching threads must
 * not keep the previous name when the incoming payload has none.
 */
export function nextSavedConversationTitle(args: {
  incomingTitle?: string | null;
  conversationId: string;
  currentConversationId: string | null;
  previousTitle: string | null;
}): string | null {
  const incoming = args.incomingTitle?.trim() || null;
  if (incoming) {
    return incoming;
  }
  if (args.conversationId === args.currentConversationId) {
    return args.previousTitle;
  }
  return null;
}

/**
 * History clicks close the list before the fetch lands. Apply the clicked
 * row's title immediately so the previous name does not flash in the header.
 */
export function chromeForHistoryOpen(args: {
  conversationId: string;
  currentConversationId: string | null;
  incomingTitle?: string | null;
  previousTitle: string | null;
}): { title: string | null; clearPreview: boolean; adoptId: boolean } {
  const incoming = args.incomingTitle?.trim() || null;
  const isSameThread = args.conversationId === args.currentConversationId;
  if (isSameThread) {
    return { title: incoming ?? args.previousTitle, clearPreview: false, adoptId: false };
  }
  return { title: incoming, clearPreview: true, adoptId: true };
}

/**
 * Adopt the first user prompt as chrome only when it newly appears. Later
 * component-list updates (polling, leftover messages during a switch) must not
 * copy that text back after an explicit title clear.
 */
export function firstUserPreviewFromThread(args: {
  components: Array<{ type: string; content: unknown }>;
  previousHadUser: boolean;
}): { preview: string | undefined; hadUser: boolean } {
  const firstUser = args.components.find((component) => component.type === 'user');
  const hadUser = Boolean(firstUser);
  if (hadUser && !args.previousHadUser && firstUser) {
    return { preview: extractContentText(firstUser.content), hadUser };
  }
  return { preview: undefined, hadUser };
}
